import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  NumberRecord,
  SipAccountRecord,
  SipConfigView,
  StaffRecord,
  TelephonyDashboard
} from "../lib/api";

const TABS = [
  "overview",
  "numbers",
  "sip",
  "assignments",
  "forwarding",
  "routing",
  "transfers",
  "audit"
] as const;

type Tab = (typeof TABS)[number];

function lifecycleLabel(status?: string) {
  return String(status || "AVAILABLE").replace(/_/g, " ");
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

export function AdminTelephonyPage({ csrf }: { csrf: string }) {
  const [tab, setTab] = useState<Tab>("overview");
  const [data, setData] = useState<TelephonyDashboard | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [search, setSearch] = useState({ country: "US", areaCode: "", region: "", locality: "", numberType: "Local" });
  const [results, setResults] = useState<Array<Record<string, unknown>>>([]);
  const [confirmNumber, setConfirmNumber] = useState<Record<string, unknown> | null>(null);
  const [assign, setAssign] = useState<Record<string, string>>({});
  const [sipView, setSipView] = useState<SipConfigView | null>(null);
  const [sipWarning, setSipWarning] = useState("");
  const [releaseTarget, setReleaseTarget] = useState<NumberRecord | null>(null);

  async function load() {
    const dash = await api.telephonyDashboard();
    setData(dash);
  }

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed to load telephony"));
  }, []);

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setError("");
    setMsg("");
    try {
      await fn();
      await load();
    } catch (e: unknown) {
      const err = e as Error & { data?: { preview?: Record<string, unknown>; code?: string } };
      if ((err as { status?: number }).status === 409 || err.message?.includes("Confirm")) {
        setError(err.message);
      } else {
        setError(err.message || "Request failed");
      }
    } finally {
      setBusy("");
    }
  }

  async function onSearch(e: FormEvent) {
    e.preventDefault();
    await run("search", async () => {
      const result = await api.searchTwilioNumbers(search, csrf);
      setResults(result.items || []);
      setMsg(`Search returned ${result.items?.length || 0} numbers. No numbers were purchased.`);
    });
  }

  const staff = data?.staff || [];
  const numbers = data?.numbers || [];

  return (
    <div>
      <div className="page-header">
        <div>
          <p className="muted">
            <Link to="/admin">Admin</Link> / Telephony
          </p>
          <h1>Telephony</h1>
          <p>Twilio business numbers, FalconPBX SIP accounts, and personal forwarding. Campaign Press-1 routing is unchanged.</p>
        </div>
        <button className="btn ghost" type="button" onClick={() => load().catch((e) => setError(e.message))}>
          Refresh
        </button>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {msg ? <div className="alert success">{msg}</div> : null}

      <div className="grid-stats">
        <div className="stat-card"><div className="label">Total numbers</div><div className="value">{data?.totalNumbers ?? 0}</div></div>
        <div className="stat-card"><div className="label">Assigned</div><div className="value">{data?.assignedNumbers ?? 0}</div></div>
        <div className="stat-card"><div className="label">Unassigned</div><div className="value">{data?.unassignedNumbers ?? 0}</div></div>
        <div className="stat-card"><div className="label">Active SIP</div><div className="value">{data?.activeSipAccounts ?? 0}</div></div>
        <div className="stat-card"><div className="label">Registered SIP</div><div className="value">{data?.registeredSipDevices ?? 0}</div></div>
        <div className="stat-card"><div className="label">Unregistered SIP</div><div className="value">{data?.unregisteredSipDevices ?? 0}</div></div>
        <div className="stat-card"><div className="label">Verified forwarding</div><div className="value">{data?.verifiedForwardingNumbers ?? 0}</div></div>
        <div className="stat-card"><div className="label">Failed routes</div><div className="value">{data?.failedRoutes ?? 0}</div></div>
        <div className="stat-card"><div className="label">Active transfers</div><div className="value">{data?.activeTransfers ?? 0}</div></div>
      </div>

      <div className="row-actions" style={{ marginBottom: "1rem" }}>
        {TABS.map((item) => (
          <button key={item} className={tab === item ? "btn sm" : "btn sm ghost"} type="button" onClick={() => setTab(item)}>
            {item}
          </button>
        ))}
      </div>

      {tab === "overview" || tab === "numbers" ? (
        <div className="card">
          <h2>Request new number</h2>
          <p className="muted">Search uses Twilio inventory only. Searching does not purchase a number.</p>
          <form className="form-grid" onSubmit={onSearch}>
            <label>Country<input value={search.country} onChange={(e) => setSearch({ ...search, country: e.target.value })} /></label>
            <label>Area code<input value={search.areaCode} onChange={(e) => setSearch({ ...search, areaCode: e.target.value })} placeholder="305" /></label>
            <label>Region<input value={search.region} onChange={(e) => setSearch({ ...search, region: e.target.value })} /></label>
            <label>Locality<input value={search.locality} onChange={(e) => setSearch({ ...search, locality: e.target.value })} /></label>
            <label>
              Number type
              <select value={search.numberType} onChange={(e) => setSearch({ ...search, numberType: e.target.value })}>
                <option>Local</option>
                <option>TollFree</option>
                <option>Mobile</option>
              </select>
            </label>
            <div className="row-actions">
              <button className="btn" type="submit" disabled={Boolean(busy)}>{busy === "search" ? "Searching…" : "Search available numbers"}</button>
              <button className="btn ghost" type="button" disabled={Boolean(busy)} onClick={() => run("sync", async () => { const r = await api.syncTwilioNumbers(csrf); setMsg(`Imported ${r.imported} existing Twilio numbers. No purchases.`); })}>Sync existing Twilio numbers</button>
            </div>
          </form>
          {results.length ? (
            <table className="spaced-table">
              <thead><tr><th>Phone number</th><th>Location</th><th>Capabilities</th><th>Pricing</th><th>Regulatory</th><th></th></tr></thead>
              <tbody>
                {results.map((row) => (
                  <tr key={String(row.phoneNumber)}>
                    <td className="mono">{String(row.phoneNumber)}</td>
                    <td>{[row.locality, row.region, row.isoCountry].filter(Boolean).join(", ")}</td>
                    <td>{row.capabilities && typeof row.capabilities === "object" ? Object.entries(row.capabilities as Record<string, boolean>).filter(([, v]) => v).map(([k]) => k).join(", ") : ""}</td>
                    <td>{row.monthlyPrice ? `${row.monthlyPrice} ${row.priceUnit || ""}` : "See Twilio"}</td>
                    <td>{String(row.addressRequirements || "none")}</td>
                    <td><button className="btn sm" type="button" onClick={() => setConfirmNumber(row)}>Provision number</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}

      {tab === "overview" || tab === "numbers" || tab === "assignments" ? (
        <div className="card">
          <h2>Phone numbers</h2>
          <table className="spaced-table">
            <thead><tr><th>Number</th><th>Status</th><th>Assigned agent</th><th>SID</th><th></th></tr></thead>
            <tbody>
              {numbers.map((n) => {
                const owner = staff.find((s) => s.userId === n.assignedAgentId);
                return (
                  <tr key={n.id}>
                    <td>
                      <div className="mono">{n.e164}</div>
                      <div className="muted">{n.friendlyName || n.label}</div>
                    </td>
                    <td><span className="badge">{lifecycleLabel(n.lifecycleStatus)}</span></td>
                    <td>
                      {owner ? `${owner.email}` : "Unassigned"}
                      <div className="row-actions" style={{ marginTop: 6 }}>
                        <select value={assign[n.id] || ""} onChange={(e) => setAssign({ ...assign, [n.id]: e.target.value })}>
                          <option value="">Assign to…</option>
                          {staff.filter((s) => s.desktopAccess !== false).map((s) => (
                            <option key={s.userId} value={s.userId}>{s.sipExtension ? `Agent ${s.sipExtension}` : "Agent"} — {s.email}</option>
                          ))}
                        </select>
                        <button className="btn sm" type="button" disabled={!assign[n.id] || Boolean(busy)} onClick={() => run("assign", async () => { await api.assignNumber(n.id, assign[n.id], csrf); setMsg("Number assigned."); })}>Assign</button>
                      </div>
                    </td>
                    <td className="mono muted">{n.providerNumberSid || "—"}</td>
                    <td className="row-actions">
                      {n.assignedAgentId ? <button className="btn sm ghost" type="button" onClick={() => run("unassign", async () => { await api.unassignNumber(n.id, csrf); })}>Unassign</button> : null}
                      <button className="btn sm ghost" type="button" onClick={() => run("suspend", async () => { await api.suspendNumber(n.id, csrf); })}>Suspend</button>
                      <button className="btn sm danger" type="button" onClick={() => setReleaseTarget(n)}>Release…</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "sip" ? (
        <div className="card">
          <h2>SIP accounts</h2>
          <p className="muted">Passwords are hidden. Reveal and reset are admin-only and audit logged.</p>
          <table className="spaced-table">
            <thead><tr><th>Agent</th><th>Username</th><th>Domain</th><th>Port</th><th>Transport</th><th>Registration</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {(data?.sipAccounts || []).map((s: SipAccountRecord) => (
                <tr key={s.id}>
                  <td>{s.agentEmail || s.agentUserId || "—"}</td>
                  <td className="mono">{s.sipUsername}</td>
                  <td className="mono">{s.sipDomain}</td>
                  <td>{s.sipPort}</td>
                  <td>{String(s.sipTransport || "").toUpperCase()}</td>
                  <td>{s.registrationStatus || "UNKNOWN"}</td>
                  <td>{s.accountStatus}</td>
                  <td><button className="btn sm" type="button" onClick={() => run("sip", async () => { const r = await api.sipConfig(s.id, false, csrf); setSipView(r.config); setSipWarning(""); })}>View config</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "forwarding" ? (
        <div className="card">
          <h2>Personal number verification</h2>
          <table className="spaced-table">
            <thead><tr><th>Agent</th><th>Forwarding</th><th>Status</th></tr></thead>
            <tbody>
              {staff.map((s: StaffRecord) => (
                <tr key={s.userId}>
                  <td>{s.email}</td>
                  <td className="mono">{s.forwardingNumberMasked || s.forwardingNumber || "—"}</td>
                  <td>{s.forwardingNumberVerified ? "VERIFIED" : "NOT VERIFIED"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "routing" || tab === "transfers" ? (
        <div className="card">
          <h2>Transfers</h2>
          <table className="spaced-table">
            <thead><tr><th>Call</th><th>Owner</th><th>Result</th><th>Updated</th></tr></thead>
            <tbody>
              {(data?.transfers || []).map((t) => (
                <tr key={String(t.id)}>
                  <td className="mono">{String(t.callId || "").slice(0, 8)}</td>
                  <td className="mono">{String(t.ownerAgentId || "")}</td>
                  <td>{String(t.transferResult || "")}</td>
                  <td>{t.updatedAt ? new Date(String(t.updatedAt)).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "audit" ? (
        <div className="card">
          <h2>Audit log</h2>
          <table className="spaced-table">
            <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Resource</th></tr></thead>
            <tbody>
              {(data?.audit || []).map((a) => (
                <tr key={String(a.id)}>
                  <td>{a.at || a.timestamp ? new Date(String(a.at || a.timestamp)).toLocaleString() : "—"}</td>
                  <td className="mono">{String(a.actorUserId || a.userId || "")}</td>
                  <td>{String(a.action || "")}</td>
                  <td>{String(a.resource || "")} {String(a.resourceId || a.e164 || "")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {confirmNumber ? (
        <div className="admin-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmNumber(null); }}>
          <div className="admin-modal" role="dialog">
            <h2>Provision number</h2>
            <p className="mono">{String(confirmNumber.phoneNumber)}</p>
            <p>This will create a paid Twilio number on the production account.</p>
            <div className="row-actions">
              <button className="btn ghost" type="button" onClick={() => setConfirmNumber(null)}>Cancel</button>
              <button className="btn danger" type="button" disabled={Boolean(busy)} onClick={() => run("provision", async () => {
                await api.provisionTwilioNumber({ phoneNumber: confirmNumber.phoneNumber, confirm: true, friendlyName: confirmNumber.friendlyName }, csrf);
                setConfirmNumber(null);
                setResults([]);
                setMsg("Number provisioned.");
              })}>Confirm provision</button>
            </div>
          </div>
        </div>
      ) : null}

      {releaseTarget ? (
        <div className="admin-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setReleaseTarget(null); }}>
          <div className="admin-modal" role="dialog">
            <h2>Release number</h2>
            <p className="mono">{releaseTarget.e164}</p>
            <p>This permanently releases the Twilio number from the production account. Unassigning is a separate, safer action.</p>
            <div className="row-actions">
              <button className="btn ghost" type="button" onClick={() => setReleaseTarget(null)}>Cancel</button>
              <button className="btn danger" type="button" onClick={() => run("release", async () => { await api.releaseNumber(releaseTarget.id, true, csrf); setReleaseTarget(null); })}>Confirm release</button>
            </div>
          </div>
        </div>
      ) : null}

      {sipView ? (
        <div className="admin-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setSipView(null); }}>
          <div className="admin-modal" role="dialog">
            <h2>FalconPBX SIP configuration</h2>
            {sipWarning ? <div className="alert error">{sipWarning}</div> : null}
            <p>Agent: {sipView.agentEmail || sipView.agentUserId}</p>
            <p>Assigned number: {sipView.assignedNumber || "—"}</p>
            <p>SIP domain: <span className="mono">{sipView.sipDomain}</span></p>
            <p>SIP username: <span className="mono">{sipView.sipUsername}</span></p>
            <p>SIP password: <span className="mono">{sipView.sipPassword}</span></p>
            <p>SIP port: {sipView.sipPort}</p>
            <p>Transport: {String(sipView.sipTransport || "").toUpperCase()}</p>
            <div className="row-actions">
              <button className="btn sm" type="button" onClick={() => run("reveal", async () => { const accounts = data?.sipAccounts || []; const hit = accounts.find((s) => s.sipUsername === sipView.sipUsername); if (!hit) return; const r = await api.sipConfig(hit.id, true, csrf); setSipView(r.config); })}>Reveal password</button>
              <button className="btn sm ghost" type="button" onClick={() => copyText(sipView.sipUsername || "")}>Copy username</button>
              <button className="btn sm ghost" type="button" onClick={() => copyText(sipView.passwordRevealed ? sipView.sipPassword || "" : "")}>Copy password</button>
              <button className="btn sm ghost" type="button" onClick={() => copyText(sipView.sipDomain || "")}>Copy domain</button>
              <button className="btn sm ghost" type="button" onClick={() => copyText(sipView.copyFullConfig || "")}>Copy full config</button>
              <button className="btn sm danger" type="button" onClick={() => {
                if (!window.confirm("Reset SIP password? Existing SIP devices using the previous password will need to be updated.")) return;
                const hit = (data?.sipAccounts || []).find((s) => s.sipUsername === sipView.sipUsername);
                if (!hit) return;
                void run("reset", async () => {
                  const r = await api.resetSipPassword(hit.id, true, csrf);
                  setSipView(r.config);
                  setSipWarning(r.warning || "Existing SIP devices using the previous password will need to be updated.");
                });
              }}>Reset SIP password</button>
              <button className="btn sm ghost" type="button" onClick={() => setSipView(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
